import axiosInstance from "./axiosInstanceV1";

const API_BASE_URL = import.meta.env.DEV
	? "/api/v1"
	: "https://api.bodhistreams.com";

const findSseFrameEnd = (buffer) => {
	const newlineIndex = buffer.indexOf("\n\n");
	const crlfIndex = buffer.indexOf("\r\n\r\n");

	if (newlineIndex === -1) return crlfIndex;
	if (crlfIndex === -1) return newlineIndex;
	return Math.min(newlineIndex, crlfIndex);
};

const parseSseFrame = (frame) => {
	const data = frame
		.split(/\r?\n/)
		.filter((line) => line.startsWith("data:"))
		.map((line) => line.slice(5).trim())
		.join("\n");

	if (!data || data === "[DONE]") return null;
	return JSON.parse(data);
};

/**
 * Send a raw chat request to the LLM (aidcmp via backend)
 * @param {Array} messages - Array of {role, content} objects
 * @param {Object} options - Optional: model, temperature, max_tokens
 * @returns {Promise} API response with choices[0].message.content
 */
export const chatLLM = async (messages, options = {}) => {
	const response = await axiosInstance.post("/user/llm/chat", {
		messages,
		...options,
	});
	return response.data;
};

export const fetchBodhiConversations = async () => {
	const response = await axiosInstance.get("/user/llm/conversations");
	return response.data;
};

export const createBodhiConversation = async ({ title } = {}) => {
	const response = await axiosInstance.post("/user/llm/conversations", {
		title,
	});
	return response.data;
};

export const deleteBodhiConversation = async (conversationId) => {
	const response = await axiosInstance.delete(
		`/user/llm/conversations/${conversationId}`,
	);
	return response.data;
};

export const fetchBodhiMessages = async (conversationId) => {
	const response = await axiosInstance.get(
		`/user/llm/conversations/${conversationId}/messages`,
	);
	return response.data;
};

export const sendBodhiMessage = async ({ conversationId, message }) => {
	const response = await axiosInstance.post(
		"/user/llm/conversations/messages",
		{
			conversation_id: conversationId,
			message,
		},
	);
	return response.data;
};

export const streamBodhiMessage = async ({
	conversationId,
	message,
	onEvent,
	signal,
}) => {
	const response = await fetch(
		`${API_BASE_URL}/user/llm/conversations/messages/stream`,
		{
			method: "POST",
			credentials: "include",
			signal,
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				conversation_id: conversationId,
				message,
			}),
		},
	);

	if (!response.ok) {
		const errorText = await response.text().catch(() => "");
		throw new Error(errorText || "Bodhi could not reply right now.");
	}

	if (!response.body?.getReader) {
		throw new Error("Streaming is not available in this browser.");
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";

	while (true) {
		const { value, done } = await reader.read();
		if (done) break;

		buffer += decoder.decode(value, { stream: true });
		let frameEnd = findSseFrameEnd(buffer);

		while (frameEnd !== -1) {
			const frame = buffer.slice(0, frameEnd);
			const separatorLength = buffer.slice(frameEnd).startsWith("\r\n\r\n")
				? 4
				: 2;
			buffer = buffer.slice(frameEnd + separatorLength);

			const event = parseSseFrame(frame);
			if (event) {
				onEvent?.(event);
				if (event.type === "error") {
					throw new Error(event.error || "Bodhi could not reply right now.");
				}
			}

			frameEnd = findSseFrameEnd(buffer);
		}
	}
};

/**
 * Generate an album description using the backend-managed system prompt.
 * @param {Object} params
 * @param {string} params.title - Album title
 * @returns {Promise<{text: string}>}
 */
export const generateDescription = async ({ title }) => {
	const response = await axiosInstance.post("/user/llm/generate/description", {
		title,
	});
	return response.data;
};

/**
 * Generate a text-to-image prompt for an album cover using the backend-managed system prompt.
 * @param {Object} params
 * @param {string} params.title - Album title
 * @param {string} [params.description] - Optional album description for extra context
 * @param {string} [params.currentPrompt] - Existing prompt text to refine/expand instead of starting fresh
 * @param {string} [params.kind] - "album" for the dedicated album cover art style; omit for playlists
 * @returns {Promise<{text: string}>}
 */
export const generateImagePrompt = async ({
	title,
	description,
	currentPrompt,
	kind,
}) => {
	const response = await axiosInstance.post("/user/llm/generate/image-prompt", {
		title,
		description,
		current_prompt: currentPrompt,
		kind,
	});
	return response.data;
};

/**
 * Generate a short subtitle/tagline for an album cover from its title/description.
 * @param {Object} params
 * @param {string} [params.title] - Album title
 * @param {string} [params.description] - Album description
 * @returns {Promise<{text: string}>}
 */
export const generateSubtitle = async ({ title, description }) => {
	const response = await axiosInstance.post("/user/llm/generate/subtitle", {
		title,
		description,
	});
	return response.data;
};

/**
 * Suggest genre tags for an album. Jev scores every tag in the catalog against the
 * album's title, subtitle, description and (for an existing album) its track titles.
 * @param {Object} params
 * @param {string} [params.title] - Album title
 * @param {string} [params.subtitle] - Album subtitle
 * @param {string} [params.description] - Album description
 * @param {string} [params.albumId] - Existing album id, adds its track titles
 * @returns {Promise<{tags: Array<{id: string, name: string, probability: number, suggested: boolean}>, model: string}>}
 */
export const suggestAlbumTags = async ({
	title,
	subtitle,
	description,
	albumId,
}) => {
	const response = await axiosInstance.post("/user/llm/generate/tags", {
		title,
		subtitle,
		description,
		album_id: albumId,
	});
	return response.data;
};
